import { BoxGeometry, Group, Mesh, Vector3 } from 'three';
import { SceneGroupConfig, ContornoGroup, FrontalesGroup, LateralesGroup, CapillasGroup, TraversalesGroup } from './sceneGroupConfig';
import { SceneGroupMouse } from './sceneGroupMouse';
import { BaseClass } from 'dac-web-base';
import { SceneGroupMaterials } from './sceneGroupMaterials';
import { Utils3d } from '../utils/utils3d';
import { DIRECTION } from '../../../js/structures/aqua/gridAqua';
import { LineUtils } from './lineUtils';
import { Merger } from '../utils/merger';
import { GROUP_TYPE } from '../../../js/structures/aqua/gridAqua';
import { CellDivision } from '../../cellDivision/cellDivision';
import { List } from '../../list/list';

export class SceneGroupUnit extends BaseClass {
	constructor( renderUnit ) {
		super();
		this.scene = null;
		this.controls = null;
		this.camera = null;
		this.grid = null;
		this._gridData = {
			contorno: new ContornoGroup(),
			frontales: new FrontalesGroup(),
			laterales: new LateralesGroup(),
			capillas: new CapillasGroup(),
			traversales: new TraversalesGroup()
		};
		this.renderUnit = renderUnit;
		this.enabled = false;
		this._hoveredObjects = [];
		this._selecteds = [];
		
		this.showing = {
			frontales: true,
			laterales: false,
			capillas: false,
			traversales: false
		};
	}

	async init( container ) {

		this.container = container;

		// init scene config
		new SceneGroupConfig().init( this );
		
		// init mouse events
		this._sceneMouse = new SceneGroupMouse();
		const mouse = this._sceneMouse.init( this, container );
		mouse.subscribe( 'onObjectClicked', this.#onObjectClicked.bind( this ) );
		mouse.subscribe( 'onFloorClicked', this.#onFloorClicked.bind( this ) );
		mouse.subscribe( 'onObjectHover', this.#onObjectHover.bind( this ) );
	}
	
	setEnabled( enabled ) {
		this.enabled = enabled;
		if( !this._sceneMouse ) return;
		this._sceneMouse.setEnabled( enabled );
	}

	async initScene( currentData, aquaGrid ) {
		
		this.showing = {
			frontales: true,
			laterales: false,
			capillas: false,
			traversales: false
		};

		//init grid
		await this.initGrid( aquaGrid, currentData );
	}

	async initGrid( aquaGrid ) {

		// generate grid
		this.#generateGrid( aquaGrid );

		// generate group objects (frontales, laterales, capillas, traversales)
		this.#generateGroupObjects( aquaGrid );

		Object.values( this._gridData ).forEach( g => {
			
			//add grid data to scene
			this.scene.add( g );

			// make performant
			Utils3d.makePerformant( g );

		} );

		Utils3d.fitOrthoCamera( this.scene, this.camera, this.container );

		this.renderUnit.render();
	}

	#generateGrid( grid ){

		//clean grid
		this.#cleanGrid();

		this.grid = grid;

		this._gridData.contorno = LineUtils.Instance.createGridShape( grid );
	}	

	#generateGroupObjects( grid ) {
		
		const outlineSize = 0.35;

		const makeMesh = ( size, material ) => {
			const group = new Group();
			//create a box
			const geomB = new BoxGeometry(size.x - outlineSize, size.y - outlineSize, size.z - outlineSize );
			const objB = new Mesh( geomB, material );
			group.add( objB );
			//create an outline
			const geomO = new BoxGeometry(size.x, size.y * 0.5, size.z );
			const objO = new Mesh( geomO, SceneGroupMaterials.OutlineMaterial );
			group.add( objO );			
			//merge and return
			return new Merger().merge( group );
		};

		// create frontales objects
		for( let i = 0; i < grid.frontales.length; i++ ) {
			const frontal = grid.frontales[i];
			const firstCell = frontal.cells.find( c => c.MIDColumna === frontal.inicio );
			const lastCell = frontal.cells.find( c => c.MIDColumna === frontal.fin );

			const size = { 
				x: ( lastCell.position.x + lastCell.MIDAncho / 2 )  - ( firstCell.position.x - firstCell.MIDAncho / 2 ),
				y: 2,
				z: 2
			};
			const position = {
				x: (firstCell.position.x - firstCell.MIDAncho / 2 ) + size.x / 2,
				y: size.y / 2,
				z: firstCell.position.z + ( frontal.direction === DIRECTION.SOUTH ? (size.z / 2 + firstCell.MIDLargo / 2) : -(size.z / 2 + firstCell.MIDLargo / 2) )
			};

			const data = makeMesh( size, SceneGroupMaterials.FrontalesMaterial );
			data.position.set( position.x, position.y, position.z );
			data.userData.aqua = frontal;

			this._gridData.frontales.add( data );
		}
		this._gridData.frontales.visible = this.showing.frontales;

		//create laterales objects
		for( let i = 0; i < grid.laterales.length; i++ ) {
			const lateral = grid.laterales[i];
			const firstCell = grid.cells.find( c => c.MIDColumna === lateral.capilla && c.MIDFila === lateral.inicio );
			const lastCell = grid.cells.find( c => c.MIDColumna === lateral.capilla && c.MIDFila === lateral.fin );

			const size = { 
				x: 2,
				y: 2,
				z: ( firstCell.position.z + firstCell.MIDLargo / 2 ) - ( lastCell.position.z - lastCell.MIDLargo / 2 ) 
			};
			const position = {
				x: firstCell.position.x + ( lateral.direction === DIRECTION.WEST ? -(size.x / 2 + firstCell.MIDAncho / 2) : (size.x / 2 + firstCell.MIDAncho / 2) ),
				y: size.y / 2,
				z: (lastCell.position.z - firstCell.MIDLargo / 2 ) + size.z / 2				
			};

			const data = makeMesh( size, SceneGroupMaterials.LateralesMaterial );
			data.position.set( position.x, position.y, position.z );
			data.userData.aqua = lateral;
			this._gridData.laterales.add( data );
		}
		this._gridData.laterales.visible = this.showing.laterales;
		
		//create capillas objects
		for( let i = 0; i < grid.capillas.length; i++ ) {
			const group = grid.capillas[i];

			const max = new Vector3( -Infinity, -Infinity, -Infinity );
			const min = new Vector3( Infinity, Infinity, Infinity );
			group.capillas.forEach( capilla => {
				capilla.cells.forEach( cell => {
					max.x = Math.max( max.x, cell.position.x + cell.MIDAncho / 2 );
					max.z = Math.max( max.z, cell.position.z + cell.MIDLargo / 2 );

					min.x = Math.min( min.x, cell.position.x - cell.MIDAncho / 2 );
					min.z = Math.min( min.z, cell.position.z - cell.MIDLargo / 2 );
				} );
			} );

			const size = { 
				x: max.x - min.x,
				y: 2,
				z: max.z - min.z
			};
			const position = {
				x: min.x + size.x / 2,
				y: size.y / 2,
				z: min.z + size.z / 2				
			};

			const mesh = makeMesh( size, SceneGroupMaterials.CapillasMaterial );
			mesh.position.set( position.x, position.y, position.z );
			mesh.userData.aqua = group;
			this._gridData.capillas.add( mesh );
		}
		this._gridData.capillas.visible = this.showing.capillas;
		
		//create traversales objects
		for( let i = 0; i < grid.transversales.length; i++ ) {
			const group = grid.transversales[i];
			const max = new Vector3( -Infinity, -Infinity, -Infinity );
			const min = new Vector3( Infinity, Infinity, Infinity );
			group.transversales.forEach( transversal => {
				transversal.cells.forEach( cell => {
					max.x = Math.max( max.x, cell.position.x + cell.MIDAncho / 2 );
					max.z = Math.max( max.z, cell.position.z + cell.MIDLargo / 2 );

					min.x = Math.min( min.x, cell.position.x - cell.MIDAncho / 2 );
					min.z = Math.min( min.z, cell.position.z - cell.MIDLargo / 2 );
				} );
			} );

			const size = { 
				x: max.x - min.x,
				y: 2,
				z: max.z - min.z
			};
			const position = {
				x: min.x + size.x / 2,
				y: size.y / 2,
				z: min.z + size.z / 2				
			};

			const mesh = makeMesh( size, SceneGroupMaterials.TraversalesMaterial );
			mesh.position.set( position.x, position.y, position.z );
			mesh.userData.aqua = group;
			this._gridData.traversales.add( mesh );
		}
		this._gridData.traversales.visible = this.showing.traversales;		
	}

	showObject( type, show ) {
		switch( type ) {
			case GROUP_TYPE.FRONTALES:
				this.showing.frontales = show;
				this._gridData.frontales.visible = show;
				break;
			case GROUP_TYPE.LATERALES:
				this.showing.laterales = show;
				this._gridData.laterales.visible = show;
				break;
			case GROUP_TYPE.CAPILLAS:
				this.showing.capillas = show;
				this._gridData.capillas.visible = show;
				break;
			case GROUP_TYPE.TRAVERSALES:
				this.showing.traversales = show;
				this._gridData.traversales.visible = show;
				break;
		}
	}

	async #onObjectClicked( data ) {
		const obj3d = data.intersect.meshes[0].object;
		const aquaObj = obj3d.userData.aqua;
		switch( data.type ) {
			case GROUP_TYPE.FRONTALES: 
			case GROUP_TYPE.LATERALES: 
			case GROUP_TYPE.CAPILLAS: 
			case GROUP_TYPE.TRAVERSALES: {
				await this.trigger( 'onObjectSelected', { aqua: aquaObj, type: data.type } );
				this.clearSelecteds();
				this.#selectGroup( obj3d, aquaObj, data.type );
			} break;
		}
		
		this.renderUnit.render();
	}

	async onObjectSelectedFromOutside( aqua, type ) {
		let obj3d = null;
		this.scene.traverse( c => {
			if( obj3d ) return;
			if( c.userData.aqua === aqua ) obj3d = c;
		} );
		if( !obj3d ) return;
		this.clearSelecteds();
		this.#selectGroup( obj3d, aqua, type );
	}

	async #onFloorClicked() {
		this.clearSelecteds();
		this.#clearHoveredObjects();
		
		await this.trigger( 'onObjectSelected', null );
	}

	#onObjectHover( data ) {
		this.#clearHoveredObjects();
		if(data.type === GROUP_TYPE.FRONTALES || 
			data.type === GROUP_TYPE.LATERALES ||
			data.type === GROUP_TYPE.CAPILLAS ||
			data.type === GROUP_TYPE.TRAVERSALES ) {
			const obj = data.intersect.meshes[0].object;
			if( obj.userData.selected ) return;

			obj.traverse( child => {
				if( child.isMesh ) {
					if( !child.userData.originalMat ) child.userData.originalMat = child.material;
					this._hoveredObjects.push( { c: child } );
					child.material = SceneGroupMaterials.HoverMaterial;
					child.needsUpdate = true;
				}
			} );
			this.container.style.cursor = 'pointer';
		} else {
			this.container.style.cursor = '';
		}
	}

	#clearHoveredObjects() {
		for( let i = 0; i < this._hoveredObjects.length; i++ ) {
			const h = this._hoveredObjects[i];
			h.c.material = h.c.userData.originalMat;
			h.c.needsUpdate = true;
		}
		this._hoveredObjects = [];

		//we may have removed a selected mat, so set selected as selectedmaterials again
		for( let i = 0; i < this._selecteds.length; i++ ) {
			const s = this._selecteds[i];
			s.c.traverse( child => {
				if( child.isMesh ) {					
					child.material = SceneGroupMaterials.SelectedMaterial;
					child.needsUpdate = true;
				}
			} );
		}
	}

	clearSelecteds() {
		for( let i = 0; i < this._selecteds.length; i++ ) {
			const s = this._selecteds[i];
			for( let j = 0; j < s.mats.length; j++ ) {
				const m = s.mats[j];
				m.c.material = m.c.userData.originalMat;
				m.c.needsUpdate = true;
			}
			
			delete s.c.userData.selected;
			if( s.html && s.html.parentNode ) s.html.parentNode.removeChild( s.html );
		}
		this._selecteds = [];
	}

	#selectGroup( obj3d, aqua, type ) {
		this.clearSelecteds();

		const html = document.createElement( 'div' );
		const selection = {
			c: obj3d,
			aqua: aqua,
			type: type,
			mats: [],
			html : html,
		};
		
		//create html
		html.classList.add( 'listactions' );
		const chtml = document.createElement( 'div' );
		const dhtml = document.createElement( 'div' );
		chtml.classList.add( 'listDivide', 'toolboxButton', 'material-icons' );
		dhtml.classList.add( 'listDelete', 'toolboxButton', 'material-icons' );
		chtml.innerHTML = 'content_cut';
		dhtml.innerHTML = 'delete';
		html.appendChild( chtml );
		html.appendChild( dhtml );
		this.container.appendChild( html );

		const pos2d = this.toScreenXY( obj3d.position, this.camera, this.container );

		html.style.cssText = `
			position: fixed;
			left: ${pos2d.x}px;
			top: ${pos2d.y}px;
			display: flex;
		`;
		this.container.querySelector( '.listDivide' ).addEventListener( 'click', async() => { await this.#showDivisionForm( ); } );
		this.container.querySelector( '.listDelete' ).addEventListener( 'click', async() => { await this.#deleteGroup( selection ); } );

		//set selected material
		obj3d.traverse( child => {
			if( child.isMesh ) {
				if( !child.userData.originalMat ) child.userData.originalMat = child.material;
				selection.mats.push( { c: child } );
				child.material = SceneGroupMaterials.SelectedMaterial;
				child.needsUpdate = true;
			}
		} );

		//set obj selected
		obj3d.userData.selected = true;
		
		this._selecteds.push( selection );
	}

	async #showDivisionForm() {
		if( this._selecteds.length === 0 ) return;

		const dat = this._selecteds[ this._selecteds.length - 1 ].aqua;
		
		if( dat.inicio === dat.fin ) return;

		const division = await new CellDivision( 'modalContainer' ).show( dat.inicio, dat.fin );

		if ( !division.ok ) return;

		this.#divideGroup( dat, division.newInit, division.newEnd );
	}

	async #divideGroup( aqua, newInit, newEnd ) {
		const type = List.getOperationType( aqua );
		if( aqua.inicio === aqua.fin ) return;
				
		const parts = List.cutInHalf( aqua, newInit, newEnd );

		//remove the original and add the parts
		const list = type === GROUP_TYPE.FRONTALES ? this.grid.frontales :
			type === GROUP_TYPE.LATERALES ? this.grid.laterales : 
				type === GROUP_TYPE.CAPILLAS ? this.grid.capillas :
					type === GROUP_TYPE.TRAVERSALES ? this.grid.transversales : [];

		const index = list.findIndex( l => l === aqua );
		if( index !== -1 ) 
			list.splice( index, 1, ...parts );

		//clear selected
		this.clearSelecteds();

		//redraw scene
		await this.initGrid( this.grid );

		//launch event
		this.trigger( 'onObjectSelected', { aqua: parts[0], type: type } );
		this.onObjectSelectedFromOutside( parts[0], type );
	}

	async #deleteGroup( selection ) {
		
		const aqua = selection.aqua;
		const type = selection.type;
		const list = type === GROUP_TYPE.FRONTALES ? this.grid.frontales :
			type === GROUP_TYPE.LATERALES ? this.grid.laterales : 
				type === GROUP_TYPE.CAPILLAS ? this.grid.capillas :
					type === GROUP_TYPE.TRAVERSALES ? this.grid.transversales : [];
		const index = list.findIndex( l => l === aqua );

		const backwards = aqua.inicio > aqua.fin;

		// Count how many rows there are of the same direction. If there is only one of the same direction, return
		let dats = [];

		//take into account only cells within the same horizontal or vertical line (malla or capilla)
		if( type === GROUP_TYPE.FRONTALES || type === GROUP_TYPE.LATERALES ) {
			const line = type === GROUP_TYPE.FRONTALES ? 'malla' :  type === GROUP_TYPE.LATERALES ? 'capilla' : '';
			list.forEach( d => { if( d.direction === aqua.direction && d[line] === aqua[line] ) dats.push( d ); } );
		}
		else
			list.forEach( d => dats.push( d ) );

		if( dats.length === 1 ) return;

		//merge with next if possible, otherwise with previous if possible
		let toMerge = dats.find( d => backwards ? d.fin === aqua.inicio + 1 : d.fin === aqua.inicio - 1 );
		if( !toMerge ) toMerge = dats.find( d => backwards ? d.inicio === aqua.fin - 1 : d.inicio === aqua.fin + 1 );
		toMerge.inicio = backwards ? Math.max( toMerge.inicio, aqua.inicio ) : Math.min( toMerge.inicio, aqua.inicio );
		toMerge.fin = backwards ? Math.min( toMerge.fin, aqua.fin ) : Math.max( toMerge.fin, aqua.fin );

		const listName = type === GROUP_TYPE.FRONTALES || type === GROUP_TYPE.LATERALES ? 'cells' : 
			type === GROUP_TYPE.TRAVERSALES ? 'transversales' :	
				type === GROUP_TYPE.CAPILLAS ? 'capillas' : '';

		toMerge[listName] = toMerge[listName].concat( aqua[listName] );

		//remove the original
		list.splice( index, 1 );
		
		//clear selected
		this.clearSelecteds();

		//redraw scene
		await this.initGrid( this.grid );
		

		//launch event
		this.trigger( 'onObjectSelected', { aqua: toMerge, type: type } );
		this.onObjectSelectedFromOutside( toMerge, type );
	}

	moveCutDeleteButtons() {
		for( let i = 0; i < this._selecteds.length; i++ ) {
			const s = this._selecteds[i];

			const pos2d = this.toScreenXY( s.c.position, this.camera, this.container );

			s.html.style.cssText = `
				position: fixed;
				left: ${pos2d.x}px;
				top: ${pos2d.y}px;
				display: flex;
			`;
		}
	}

	toScreenXY( position, camera, div) {
		
		const hw = div.clientWidth / 2.0;
		const hh = div.clientHeight / 2.0;

		camera.updateMatrixWorld();

		const pos2d = position.clone();
		pos2d.project( camera );
		
		var offset = this.#findOffset( div );

		pos2d.x = ( pos2d.x * hw ) + hw  + offset.left;
		pos2d.y = - ( pos2d.y * hh ) + hh  + offset.top;
	
		return pos2d;
	}

	#findOffset( html ) { 
		const pos = { left: 0, top: 0 };
		if ( !html.offsetParent ) return pos;  

		do  
		{ 
			pos.left += html.offsetLeft; 
			pos.top += html.offsetTop; 
		} while ( html === html.offsetParent ); 

		return pos;
	}

	#cleanGrid() {
		
		this.scene.remove( this._gridData.contorno );
		Utils3d.dispose3d( this._gridData.contorno );
		this._gridData.contorno = new ContornoGroup();

		this.scene.remove( this._gridData.frontales );
		Utils3d.dispose3d( this._gridData.frontales );
		this._gridData.frontales = new FrontalesGroup();

		Utils3d.dispose3d( this._gridData.laterales );
		this.scene.remove( this._gridData.laterales );
		this._gridData.laterales = new LateralesGroup();

		Utils3d.dispose3d( this._gridData.capillas );
		this.scene.remove( this._gridData.capillas );
		this._gridData.capillas = new CapillasGroup();

		Utils3d.dispose3d( this._gridData.traversales );
		this.scene.remove( this._gridData.traversales );
		this._gridData.traversales = new TraversalesGroup();
	}
}